import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';
import BottomNav from '../components/BottomNav';
import API from '../config';
import { Helmet } from 'react-helmet-async';


const OPTION_COLORS = ['#3772ff','#9b5de5','#f3a94a','#16c784','#ea3943','#00c9b1'];

const getAvatarGradient = (name) => {
  const gradients = [
    ['#3772ff','#9b5de5'],
    ['#16c784','#3772ff'],
    ['#f3a94a','#ea3943'],
    ['#9b5de5','#00c9b1'],
    ['#ea3943','#f3a94a'],
    ['#00c9b1','#16c784'],
  ];
  if (!name) return gradients[0];
  return gradients[name.charCodeAt(0) % gradients.length];
};

const fmt = n => {
  const v = parseFloat(n || 0);
  if (v >= 1e6) return `KES ${(v/1e6).toFixed(1)}M`;
  if (v >= 1e3) return `KES ${(v/1e3).toFixed(0)}K`;
  return `KES ${v.toFixed(0)}`;
};

const ago = ts => {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};

const RANGES = ['1H','6H','1D','1W','ALL'];

const sh2 = (w, h, r, T) => ({
  width: w, height: h, borderRadius: r, flexShrink: 0,
  background: `linear-gradient(90deg, ${T.surfaceHi} 25%, ${T.border} 50%, ${T.surfaceHi} 75%)`,
  backgroundSize: '400px 100%', animation: 'shimmer 1.4s infinite linear',
});

const MarketPageSkeleton = ({ T, isMobile }) => (
  <div style={{ minHeight:'100vh', background:T.bg }}>
    <style>{`
      @keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
    `}</style>

    {/* Nav placeholder */}
    <div style={{ height:56, borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', padding:'0 20px', gap:12 }}>
      <div style={sh2(100, 32, 8, T)} />
      <div style={{ flex:1 }} />
      <div style={sh2(80, 28, 7, T)} />
    </div>

    {/* Header placeholder */}
    <div style={{ borderBottom:`1px solid ${T.border}`, padding:isMobile?'16px':'20px 28px' }}>
      <div style={{ maxWidth:1200, margin:'0 auto' }}>
        <div style={sh2(140, 12, 5, T)} />
        <div style={{ display:'flex', gap:16, alignItems:'flex-start', marginTop:12 }}>
          <div style={sh2(isMobile?56:72, isMobile?56:72, 10, T)} />
          <div style={{ flex:1 }}>
            <div style={sh2('80%', 20, 6, T)} />
            <div style={{ marginTop:10, display:'flex', gap:10 }}>
              <div style={sh2(70, 30, 6, T)} />
              <div style={sh2(50, 16, 5, T)} />
            </div>
            <div style={{ marginTop:12, display:'flex', gap:10 }}>
              <div style={sh2(60, 12, 5, T)} />
              <div style={sh2(100, 12, 5, T)} />
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Body placeholder */}
    <div style={{ maxWidth:1200, margin:'0 auto', padding:isMobile?'16px':'20px 28px' }}>
      <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 320px', gap:20, alignItems:'start' }}>
        <div>
          <div style={sh2('100%', 260, 10, T)} />
          <div style={{ marginTop:24 }}>
            <div style={sh2('100%', 14, 5, T)} />
            <div style={{ marginTop:8 }}><div style={sh2('90%', 14, 5, T)} /></div>
            <div style={{ marginTop:8 }}><div style={sh2('70%', 14, 5, T)} /></div>
          </div>
        </div>
        {!isMobile && (
          <div style={sh2('100%', 320, 10, T)} />
        )}
      </div>
    </div>
  </div>
);

// ─── Selected Option Chart ────────────────────────────────────────────────────
const SelectedOptionChart = ({ data, selectedOptionId, selOption, T }) => {
  const [sTip, setSTip] = useState(null);
  const sRef = useRef(null);
  const rawRows = data?.data || [];
  if (!selectedOptionId || !rawRows.length) return null;
  const optionMap = {};
  for (const row of rawRows) {
    if (!optionMap[row.option_id]) optionMap[row.option_id] = { label: row.label, rows: [] };
    optionMap[row.option_id].rows.push(row);
  }
  const optionKeys = Object.keys(optionMap);
  const selKey = String(selectedOptionId);
  const selOpt = optionMap[selKey];
  if (!selOpt || selOpt.rows.length < 2) return null;
  const selColor = selOption?.color || OPTION_COLORS[optionKeys.indexOf(selKey) % OPTION_COLORS.length];
  const sVals  = selOpt.rows.map(r => parseFloat(r.percent));
  const sTimes = selOpt.rows.map(r => new Date(r.recorded_at).getTime());
  const sLast  = sVals[sVals.length - 1], sFirst = sVals[0], sTrend = sLast - sFirst;
  const sMin   = Math.max(0, Math.floor((Math.min(...sVals) - 5) / 5) * 5);
  const sMax   = Math.min(100, Math.ceil((Math.max(...sVals) + 5) / 5) * 5) || 10;
  const sRange = sMax - sMin || 1;
  const sW = 560, sH = 160, sPL = 44, sPR = 16, sPT = 12, sPB = 28;
  const sCW = sW - sPL - sPR, sCH = sH - sPT - sPB;
  const sToX = i => sPL + (i / (sVals.length - 1)) * sCW;
  const sToY = v => sPT + sCH - ((v - sMin) / sRange) * sCH;
  const sPts  = sVals.map((v, i) => [sToX(i), sToY(v)]);
  const sPath = sPts.map((p, i) => `${i===0?'M':'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const sArea = `${sPath} L ${sToX(sVals.length-1)} ${sPT+sCH} L ${sPL} ${sPT+sCH} Z`;
  const sGridVals = Array.from({ length: 4 }, (_, i) => Math.round(sMin + (i / 3) * sRange));
  const sXLabels = Array.from({ length: 4 }, (_, i) => {
    const idx = Math.round((i / 3) * (selOpt.rows.length - 1));
    return { x: sToX(idx), label: new Date(sTimes[idx]).toLocaleDateString([], { month: 'short', day: 'numeric' }) };
  });
  const sDotX = sPts[sPts.length-1][0], sDotY = sPts[sPts.length-1][1];
  const handleSMove = e => {
    const rect = sRef.current?.getBoundingClientRect(); if(!rect) return;
    const mx  = ((e.clientX - rect.left) / rect.width) * sW;
    const idx = Math.round(((mx - sPL) / sCW) * (sVals.length - 1));
    if (idx < 0 || idx >= sVals.length) { setSTip(null); return; }
    setSTip({ x: sToX(idx), y: sToY(sVals[idx]), pct: sVals[idx], ts: sTimes[idx] });
  };
  return (
    <div style={{ padding: '16px 20px', background: `${selColor}06`, borderTop: `1px solid ${T.border}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: selColor }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: selColor }}>{selOpt.label}</span>
        <span style={{ fontSize: 20, fontWeight: 800, color: selColor, letterSpacing: '-1px' }}>{sLast.toFixed(0)}%</span>
        <span style={{ fontSize: 12, color: sTrend >= 0 ? T.yes : T.no, fontWeight: 700 }}>{sTrend >= 0 ? '▲' : '▼'} {Math.abs(sTrend).toFixed(1)}%</span>
      </div>
      <svg ref={sRef} width="100%" viewBox={`0 0 ${sW} ${sH}`} style={{ display: 'block', overflow: 'visible', cursor: 'crosshair' }} onMouseMove={handleSMove} onMouseLeave={() => setSTip(null)}>
        <defs>
          <linearGradient id={`sFill_${selKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={selColor} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={selColor} stopOpacity="0.01"/>
          </linearGradient>
          <clipPath id={`sClip_${selKey}`}><rect x={sPL} y={sPT} width={sCW} height={sCH}/></clipPath>
        </defs>
        {sGridVals.map(v => { const y = sToY(v); return (<g key={v}><line x1={sPL} y1={y} x2={sPL+sCW} y2={y} stroke={T.border} strokeWidth="1" strokeDasharray="3,5" opacity="0.7"/><text x={sPL-8} y={y+4} textAnchor="end" fontSize="10" fill={T.textDim}>{v}%</text></g>); })}
        <line x1={sPL} y1={sPT+sCH} x2={sPL+sCW} y2={sPT+sCH} stroke={T.border} strokeWidth="1"/>
        {sXLabels.map((l, i) => <text key={i} x={l.x} y={sH-4} textAnchor="middle" fontSize="10" fill={T.textDim}>{l.label}</text>)}
        <path d={sArea} fill={`url(#sFill_${selKey})`} clipPath={`url(#sClip_${selKey})`}/>
        <path d={sPath} fill="none" stroke={selColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" clipPath={`url(#sClip_${selKey})`} pathLength="1" strokeDasharray="1" strokeDashoffset="1" style={{ animation: 'drawLine 1s cubic-bezier(0.4,0,0.2,1) forwards' }}/>
        <circle cx={sDotX} cy={sDotY} r="5" fill={selColor}/>
        <circle cx={sDotX} cy={sDotY} r="9" fill={selColor} fillOpacity="0.2"/>
        {sTip && (<><line x1={sTip.x} y1={sPT} x2={sTip.x} y2={sPT+sCH} stroke={T.borderHi} strokeWidth="1" strokeDasharray="3,3"/><circle cx={sTip.x} cy={sTip.y} r="4" fill={selColor} stroke={T.surface} strokeWidth="2"/></>)}
      </svg>
      {sTip
        ? <div style={{ background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 6, padding: '8px 12px', marginTop: 6, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: T.textDim }}>{new Date(sTip.ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
            <span style={{ fontSize: 13, color: selColor, fontWeight: 700 }}>{sTip.pct.toFixed(1)}%</span>
          </div>
        : <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <span style={{ fontSize: 11, color: selColor, fontWeight: 600 }}>{sLast.toFixed(0)}% chance</span>
          </div>
      }
    </div>
  );
};

// ─── Price Chart ──────────────────────────────────────────────────────────────
const PriceChart = ({ data, selectedOptionId, T, market }) => {
  const [range, setRange]     = useState('ALL');
  const [tooltip, setTooltip] = useState(null);
  const svgRef = useRef(null);

  const filterRows = rows => {
    if (!rows || range === 'ALL') return rows;
    const now = Date.now();
    const ms = { '1H': 3600e3, '6H': 6*3600e3, '1D': 86400e3, '1W': 7*86400e3 };
    const cutoff = now - (ms[range] || 0);
    const filtered = rows.filter(d => new Date(d.recorded_at).getTime() >= cutoff);
    return filtered.length >= 2 ? filtered : rows;
  };

  const isMulti = data?.type === 'multiple';
  const rawRows = data?.data || (Array.isArray(data) ? data : []);
  const W = 600, H = 260, PL = 44, PR = 16, PT = 16, PB = 32;
  const CW = W - PL - PR, CH = H - PT - PB;

  const RangeButtons = () => (
    <div style={{ display: 'flex', gap: 3 }}>
      {RANGES.map(r => (
        <button key={r} onClick={() => { setRange(r); setTooltip(null); }}
          style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid ${range===r ? T.accent : T.border}`, background: range===r ? `${T.accent}18` : 'transparent', color: range===r ? T.accent : T.textMuted, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.1s' }}>
          {r}
        </button>
      ))}
    </div>
  );

  const EmptyChart = () => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><RangeButtons /></div>
      <div style={{ height: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: T.textDim, gap: 8 }}>
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M4 28 L10 18 L16 22 L22 10 L28 6" stroke={T.border} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span style={{ fontSize:13 }}>Chart appears after first trades</span>
      </div>
    </div>
  );

  if (isMulti) {
    const allRows = filterRows(rawRows);
    if (!allRows || allRows.length < 2) return <EmptyChart />;
    const optionMap = {};
    for (const row of allRows) {
      if (!optionMap[row.option_id]) optionMap[row.option_id] = { label: row.label, rows: [] };
      optionMap[row.option_id].rows.push(row);
    }
    const optionKeys = Object.keys(optionMap);
    const allVals  = allRows.map(d => parseFloat(d.percent));
    const allTimes = allRows.map(d => new Date(d.recorded_at).getTime());
    const timeMin  = Math.min(...allTimes), timeMax = Math.max(...allTimes);
    const yMax     = Math.min(100, Math.ceil((Math.max(...allVals) + 5) / 10) * 10) || 10;
    const yRange   = yMax || 1;
    const toX = ts => timeMax === timeMin ? PL + CW/2 : PL + ((ts-timeMin)/(timeMax-timeMin))*CW;
    const toY = v  => PT + CH - (v/yRange)*CH;
    const gridVals = Array.from({length:5},(_,i) => Math.round((i/4)*yRange));
    const xLabels  = Array.from({length:5},(_,i) => {
      const ts = timeMin + (i/4)*(timeMax-timeMin);
      const d  = new Date(ts);
      return { x: toX(ts), label: range==='1H'||range==='6H' ? d.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : d.toLocaleDateString([],{month:'short',day:'numeric'}) };
    });
    const handleMouseMove = e => {
      const rect = svgRef.current?.getBoundingClientRect(); if(!rect) return;
      const mx = ((e.clientX-rect.left)/rect.width)*W;
      const pct = (mx-PL)/CW; if(pct<0||pct>1){setTooltip(null);return;}
      const ts = timeMin + pct*(timeMax-timeMin);
      const tips = optionKeys.map((k,i) => {
        const opt = optionMap[k];
        const closest = opt.rows.reduce((a,b) => Math.abs(new Date(a.recorded_at).getTime()-ts)<Math.abs(new Date(b.recorded_at).getTime()-ts)?a:b);
        return { label: opt.label, pct: parseFloat(closest.percent), color: OPTION_COLORS[i%OPTION_COLORS.length] };
      });
      setTooltip({ x: mx, tips, ts });
    };
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14, flexWrap:'wrap', gap:8 }}>
          <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
            {optionKeys.map((k,i) => {
              const opt = optionMap[k], last = parseFloat(opt.rows[opt.rows.length-1].percent), color = OPTION_COLORS[i%OPTION_COLORS.length];
              return (
                <div key={k} style={{ display:'flex', alignItems:'center', gap:6 }}>
                  <div style={{ width:24, height:3, borderRadius:2, background:color }}/>
                  <span style={{ fontSize:12, color:T.textMuted }}>{opt.label}</span>
                  <span style={{ fontSize:12, color, fontWeight:700 }}>{last.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
          <RangeButtons />
        </div>
        <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block', overflow:'visible', cursor:'crosshair' }} onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
          <defs><clipPath id="mClip"><rect x={PL} y={PT} width={CW} height={CH}/></clipPath></defs>
          {gridVals.map(v => { const y=toY(v); return (<g key={v}><line x1={PL} y1={y} x2={PL+CW} y2={y} stroke={T.border} strokeWidth="1" strokeDasharray="3,5" opacity="0.8"/><text x={PL-8} y={y+4} textAnchor="end" fontSize="10" fill={T.textDim}>{v}%</text></g>); })}
          <line x1={PL} y1={PT+CH} x2={PL+CW} y2={PT+CH} stroke={T.border} strokeWidth="1"/>
          {xLabels.map((l,i) => <text key={i} x={l.x} y={H-4} textAnchor="middle" fontSize="10" fill={T.textDim}>{l.label}</text>)}
          {optionKeys.map((k,i) => {
            const opt=optionMap[k], color=OPTION_COLORS[i%OPTION_COLORS.length];
            const pts=opt.rows.map(row=>[toX(new Date(row.recorded_at).getTime()),toY(parseFloat(row.percent))]);
            if(pts.length<2) return null;
            const pathD=pts.map((p,j)=>`${j===0?'M':'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
            const last=pts[pts.length-1];
            return (<g key={k}><path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#mClip)" opacity="0.9" pathLength="1" strokeDasharray="1" strokeDashoffset="1" style={{ animation: 'drawLine 1.2s cubic-bezier(0.4,0,0.2,1) forwards' }}/><circle cx={last[0]} cy={last[1]} r="4" fill={color}/></g>);
          })}
          {tooltip && tooltip.x>=PL && tooltip.x<=PL+CW && <line x1={tooltip.x} y1={PT} x2={tooltip.x} y2={PT+CH} stroke={T.borderHi} strokeWidth="1" strokeDasharray="3,3"/>}
        </svg>
        {tooltip && (
          <div style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px', marginTop:8, display:'flex', flexWrap:'wrap', gap:12 }}>
            <span style={{ fontSize:11, color:T.textDim, width:'100%' }}>{new Date(tooltip.ts).toLocaleString([],{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
            {tooltip.tips.map((t,i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:6 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:t.color}}/>
                <span style={{ fontSize:12, color:T.textMuted }}>{t.label}</span>
                <span style={{ fontSize:12, color:t.color, fontWeight:700 }}>{t.pct.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const filtered2 = filterRows(rawRows);
  if (!filtered2 || filtered2.length < 2) return <EmptyChart />;
  const vals   = filtered2.map(d => parseFloat(d.yes_percent));
  const times  = filtered2.map(d => new Date(d.recorded_at).getTime());
  const last   = vals[vals.length-1], first=vals[0], trend=last-first;
  const lineColor = trend >= 0 ? T.yes : T.no;
  const yMin   = Math.max(0, Math.floor((Math.min(...vals)-5)/5)*5);
  const yMax   = Math.min(100, Math.ceil((Math.max(...vals)+5)/5)*5) || 10;
  const yRange = yMax - yMin || 1;
  const gridVals2 = Array.from({length:5},(_,i) => Math.round(yMin+(i/4)*yRange));
  const toX2 = i  => PL + (i/(vals.length-1))*CW;
  const toY2 = v  => PT + CH - ((v-yMin)/yRange)*CH;
  const pts2  = vals.map((v,i) => [toX2(i),toY2(v)]);
  const pathD2 = pts2.map((p,i) => `${i===0?'M':'L'} ${p[0].toFixed(1)} ${p[1].toFixed(1)}`).join(' ');
  const areaD  = `${pathD2} L ${toX2(vals.length-1)} ${PT+CH} L ${PL} ${PT+CH} Z`;
  const xLabels2 = Array.from({length:5},(_,i) => {
    const idx = Math.round((i/4)*(filtered2.length-1));
    const d   = new Date(times[idx]);
    return { x: toX2(idx), label: range==='1H'||range==='6H' ? d.toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : d.toLocaleDateString([],{month:'short',day:'numeric'}) };
  });
  const handleMouseMove2 = e => {
    const rect = svgRef.current?.getBoundingClientRect(); if(!rect) return;
    const mx  = ((e.clientX-rect.left)/rect.width)*W;
    const idx = Math.round(((mx-PL)/CW)*(vals.length-1));
    if(idx<0||idx>=vals.length){setTooltip(null);return;}
    setTooltip({ x:toX2(idx), y:toY2(vals[idx]), pct:vals[idx], ts:times[idx] });
  };
  const dotX = pts2[pts2.length-1][0], dotY = pts2[pts2.length-1][1];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'baseline', gap:12 }}>
          <span style={{ fontSize:28, fontWeight:800, color:T.accent, letterSpacing:'-1px' }}>{last.toFixed(0)}%</span>
          <span style={{ fontSize:13, color:T.textMuted }}>chance {market?.yes_label||'YES'}</span>
          <span style={{ fontSize:13, color:trend>=0?T.yes:T.no, fontWeight:700 }}>{trend>=0?'▲':'▼'} {Math.abs(trend).toFixed(1)}%</span>
        </div>
        <RangeButtons />
      </div>
      <svg ref={svgRef} width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block', overflow:'visible', cursor:'crosshair' }} onMouseMove={handleMouseMove2} onMouseLeave={() => setTooltip(null)}>
        <defs>
          <linearGradient id="bFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.15"/>
            <stop offset="100%" stopColor={lineColor} stopOpacity="0.01"/>
          </linearGradient>
          <clipPath id="bClip"><rect x={PL} y={PT} width={CW} height={CH}/></clipPath>
        </defs>
        {gridVals2.map(v => { const y=toY2(v); return (<g key={v}><line x1={PL} y1={y} x2={PL+CW} y2={y} stroke={T.border} strokeWidth="1" strokeDasharray="3,5" opacity="0.8"/><text x={PL-8} y={y+4} textAnchor="end" fontSize="10" fill={T.textDim}>{v}%</text></g>); })}
        <line x1={PL} y1={PT+CH} x2={PL+CW} y2={PT+CH} stroke={T.border} strokeWidth="1"/>
        {xLabels2.map((l,i) => <text key={i} x={l.x} y={H-4} textAnchor="middle" fontSize="10" fill={T.textDim}>{l.label}</text>)}
        <path d={areaD} fill="url(#bFill)" clipPath="url(#bClip)" style={{ animation: 'fadeIn 0.8s ease forwards' }}/>
        <path d={pathD2} fill="none" stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" clipPath="url(#bClip)"
          pathLength="1" strokeDasharray="1" strokeDashoffset="1"
          style={{ animation: 'drawLine 1.2s cubic-bezier(0.4,0,0.2,1) forwards' }}/>
        <circle cx={dotX} cy={dotY} r="5" fill={lineColor}/>
        <circle cx={dotX} cy={dotY} r="10" fill={lineColor} fillOpacity="0.18"/>
        {tooltip && (<><line x1={tooltip.x} y1={PT} x2={tooltip.x} y2={PT+CH} stroke={T.borderHi} strokeWidth="1" strokeDasharray="3,3"/><circle cx={tooltip.x} cy={tooltip.y} r="5" fill={lineColor} stroke={T.surface} strokeWidth="2"/></>)}
      </svg>
      {tooltip && (
        <div style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px', marginTop:8, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:11, color:T.textDim }}>{new Date(tooltip.ts).toLocaleString([],{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</span>
          <div style={{ display:'flex', gap:16 }}>
            <span style={{ fontSize:13, color:T.yes, fontWeight:700 }}>{market?.yes_label||'YES'} {tooltip.pct.toFixed(1)}%</span>
            <span style={{ fontSize:13, color:T.no, fontWeight:700 }}>{market?.no_label||'NO'} {(100-tooltip.pct).toFixed(1)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Comments ─────────────────────────────────────────────────────────────────
const Comments = ({ marketId, token, navigate, T }) => {
  const [comments, setComments] = useState([]);
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(false);

  useEffect(() => { axios.get(`${API}/api/markets/${marketId}/comments`).then(r => setComments(r.data)).catch(() => {}); }, [marketId]);

  const submit = async () => {
    if (!text.trim() || !token) { if (!token) navigate('/login'); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/markets/${marketId}/comments`, { content: text }, { headers: { Authorization: `Bearer ${token}` } });
      setComments(p => [r.data, ...p]); setText('');
    } catch (_) {}
    setLoading(false);
  };

  const agoFn = ts => {
    const s = Math.floor((Date.now()-new Date(ts))/1000);
    if(s<60) return`${s}s`; if(s<3600) return`${Math.floor(s/60)}m`;
    if(s<86400) return`${Math.floor(s/3600)}h`; return`${Math.floor(s/86400)}d`;
  };

  return (
    <div>
      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        <input value={text} onChange={e => setText(e.target.value)} onKeyDown={e => e.key==='Enter'&&submit()}
          placeholder={token ? 'Share your take...' : 'Sign in to comment'} disabled={!token} maxLength={1000}
          style={{ flex:1, background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 12px', color:T.text, fontSize:13, outline:'none', fontFamily:'inherit' }} />
        <button onClick={submit} disabled={loading||!text.trim()||!token}
          style={{ background:text.trim()&&token?T.accent:T.surfaceHi, color:text.trim()&&token?'#fff':T.textDim, border:'none', borderRadius:7, padding:'10px 16px', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
          Post
        </button>
      </div>
      {!token && <p style={{ color:T.textDim, fontSize:13, marginBottom:16, textAlign:'center' }}><span onClick={() => navigate('/login')} style={{ color:T.accent, cursor:'pointer', fontWeight:600 }}>Sign in</span> to join the discussion</p>}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {comments.length===0
          ? <p style={{ color:T.textDim, fontSize:13, textAlign:'center', padding:'24px 0' }}>No comments yet. Be first!</p>
          : comments.map(c => (
            <div key={c.id} style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, padding:'12px 14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:28, height:28, borderRadius:'50%', background:`linear-gradient(135deg, ${getAvatarGradient(c.full_name)[0]}, ${getAvatarGradient(c.full_name)[1]})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
                    {(c.username||c.full_name||'?')[0].toUpperCase()}
                  </div>
                  <span style={{ fontSize:13, fontWeight:600, color:T.accent }}>{c.username?`@${c.username}`:c.full_name}</span>
                </div>
                <span style={{ fontSize:11, color:T.textDim }}>{agoFn(c.created_at)}</span>
              </div>
              <p style={{ fontSize:13, color:T.text, lineHeight:1.5 }}>{c.content}</p>
            </div>
          ))
        }
      </div>
    </div>
  );
};

// ─── Activity Feed ────────────────────────────────────────────────────────────
const ActivityFeed = ({ marketId, market, T }) => {
  const [activity, setActivity] = useState([]);
  useEffect(() => { axios.get(`${API}/api/markets/${marketId}/activity`).then(r => setActivity(r.data)).catch(() => {}); }, [marketId]);

  if (!activity.length) return <p style={{ color:T.textDim, fontSize:13, textAlign:'center', padding:'24px 0' }}>No activity yet</p>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:1 }}>
      {activity.map((a,i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${T.border}` }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:28, height:28, borderRadius:'50%', background:`linear-gradient(135deg, ${getAvatarGradient(a.full_name)[0]}, ${getAvatarGradient(a.full_name)[1]})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
              {(a.username||a.full_name||'?')[0].toUpperCase()}
            </div>
            <div>
              <span style={{ fontSize:13, color:T.text, fontWeight:500 }}>
  {a.chama_name
    ? <span style={{ display:'inline-flex', alignItems:'center', gap:5 }}>
        <span style={{ fontSize:10, background:'rgba(167,139,250,0.15)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.3)', borderRadius:4, padding:'1px 6px', fontWeight:700 }}>CHAMA</span>
        {a.chama_name}
      </span>
    : (a.username ? `@${a.username}` : a.full_name)
  }
</span>
              <span style={{ fontSize:13, color:T.textMuted }}> predicted </span>
              <span style={{ fontSize:13, fontWeight:700, color:a.prediction==='yes'?T.yes:T.no }}>
                {a.option_label||(a.prediction==='yes'?(market?.yes_label||'YES'):(market?.no_label||'NO'))}
              </span>
            </div>
          </div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <p style={{ fontSize:13, fontWeight:700, color:T.text }}>{`KES ${parseFloat(a.amount).toLocaleString()}`}</p>
            <p style={{ fontSize:11, color:T.textDim }}>{ago(a.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// Mirrors MarketPage's getProbs() exactly
const getMarketProbs = (m, priceHistory) => {
  if (m.market_type === 'multiple' && m.options?.length) {
    const rawRows = priceHistory?.data || [];
    const totalPool = m.options.reduce((s, o) => s + parseFloat(o.total_amount || 0), 0);
    return m.options.map(o => {
      const optRows = rawRows.filter(r => String(r.option_id) === String(o.id));
      const latestPct = optRows.length ? parseFloat(optRows[optRows.length - 1].percent) : null;
      return {
        ...o,
        prob: latestPct !== null
          ? Math.round(latestPct)
          : totalPool > 0
            ? Math.round((parseFloat(o.total_amount || 0) / totalPool) * 100)
            : Math.round(100 / m.options.length),
      };
    });
  }
  const y = parseFloat(m.total_yes || 0), n = parseFloat(m.total_no || 0), t = y + n;
  const yp = m.yes_price ? Math.round(parseFloat(m.yes_price) * 100) : t > 0 ? Math.round((y / t) * 100) : 50;
  return [{ label: 'YES', prob: yp }, { label: 'NO', prob: 100 - yp }];
};

// ─── Related Markets ──────────────────────────────────────────────────────────
const RelatedMarkets = ({ category, currentId, navigate, T }) => {
  const [related, setRelated] = useState([]);
  const [historyMap, setHistoryMap] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/markets?sort=newest`)
      .then(async r => {
        const list = r.data.filter(m => m.category===category && m.id!==parseInt(currentId) && m.status==='open').slice(0,3);
        setRelated(list);
        const entries = await Promise.all(list.map(m =>
          axios.get(`${API}/api/markets/${m.id}/price-history`).then(res => [m.id, res.data]).catch(() => [m.id, null])
        ));
        setHistoryMap(Object.fromEntries(entries));
      })
      .catch(() => {});
  }, [category, currentId]);

  if (!related.length) return null;

  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden', marginTop:12 }}>
      <div style={{ padding:'12px 16px', borderBottom:`1px solid ${T.border}` }}>
        <p style={{ fontSize:11, fontWeight:700, color:T.textMuted, textTransform:'uppercase', letterSpacing:'0.07em' }}>Related Markets</p>
      </div>
      {related.map((m,i) => {
        const isMulti = m.market_type === 'multiple';
        const probs = getMarketProbs(m, historyMap[m.id]);
        let prob, probLabel, color;
        if (isMulti) {
          const top = probs.reduce((a, b) => (b.prob > a.prob ? b : a), probs[0]);
          prob = top.prob;
          probLabel = top.label;
          color = T.accent;
        } else {
          const yp = probs[0].prob;
          const leading = yp >= 50;
          prob = leading ? yp : 100 - yp;
          probLabel = leading ? (m.yes_label || 'Yes') : (m.no_label || 'No');
          color = leading ? T.yes : T.no;
        }
        return (
          <div key={m.id} onClick={() => navigate(`/markets/${m.id}`)}
            style={{ display:'flex', alignItems:'center', gap:10, padding:'12px 16px', borderBottom:i<related.length-1?`1px solid ${T.border}`:'none', cursor:'pointer', transition:'background 0.1s' }}
            onMouseEnter={e => e.currentTarget.style.background=T.surfaceHi}
            onMouseLeave={e => e.currentTarget.style.background='transparent'}>
            <div style={{ width:32, height:32, borderRadius:6, overflow:'hidden', flexShrink:0, background:T.surfaceHi, border:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {m.image_url ? <img src={m.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} onError={e=>e.target.style.display='none'}/> : <span style={{ fontSize:9, fontWeight:800, color:T.textDim }}>{m.category?.slice(0,3).toUpperCase()}</span>}
            </div>
            <div style={{ flex:1, minWidth:0 }}>
              <p style={{ fontSize:12, fontWeight:600, color:T.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:4 }}>{m.title}</p>
              <span style={{ fontSize:11, color, fontWeight:700, background:`${color}15`, border:`1px solid ${color}33`, borderRadius:4, padding:'2px 6px' }}>{probLabel} {prob}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ─── Live Bet Ticker ──────────────────────────────────────────────────────────
const LiveBetTicker = ({ marketId, market, T, token }) => {
  const [bets, setBets] = useState([]);
  useEffect(() => { axios.get(`${API}/api/markets/${marketId}/activity`).then(r => setBets(r.data.slice(0,3))).catch(() => {}); }, [marketId]);
  useEffect(() => {
    const es = new EventSource(`${API}/api/bets/live-feed?market_id=${marketId}`);
    es.onmessage = () => { axios.get(`${API}/api/markets/${marketId}/activity`).then(r => setBets(r.data.slice(0,3))).catch(() => {}); };
    es.onerror = () => es.close();
    return () => es.close();
  }, [marketId]);

  if (!bets.length) return null;

  return (
    <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden', marginTop:12 }}>
<div style={{ padding:'10px 14px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', gap:6 }}>
        <span style={{ fontSize:11, fontWeight:700, color:T.textMuted, textTransform:'uppercase', letterSpacing:'0.07em' }}>Live Trades</span>
      </div>
      {bets.map((b,i) => (
        <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 14px', borderBottom:i<bets.length-1?`1px solid ${T.border}`:'none' }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, minWidth:0 }}>
            <div style={{ width:24, height:24, borderRadius:'50%', background:`linear-gradient(135deg, ${getAvatarGradient(b.chama_name || b.full_name)[0]}, ${getAvatarGradient(b.chama_name || b.full_name)[1]})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#fff', flexShrink:0 }}>
              {b.chama_name ? '👤👤👤' : (b.username||b.full_name||'?')[0].toUpperCase()}
            </div>
            <div style={{ minWidth:0 }}>
              {b.chama_name ? (
                <div style={{ display:'flex', alignItems:'center', gap:5 }}>
                  <span style={{ fontSize:10, background:'rgba(167,139,250,0.15)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.3)', borderRadius:4, padding:'1px 5px', fontWeight:700 }}>CHAMA</span>
                  <span style={{ fontSize:12, color:T.text, fontWeight:600 }}>{b.chama_name}</span>
                </div>
              ) : (
                <span style={{ fontSize:12, color:T.text, fontWeight:600 }}>{b.username?`@${b.username}`:b.full_name}</span>
              )}
              <span style={{ fontSize:11, color:T.textDim }}> · </span>
              <span style={{ fontSize:11, fontWeight:700, color:b.prediction==='yes'?T.yes:T.no }}>
                {b.option_label||(b.prediction==='yes'?(market?.yes_label||'YES'):(market?.no_label||'NO'))}
              </span>
            </div>
          </div>
          <div style={{ textAlign:'right', flexShrink:0 }}>
            <p style={{ fontSize:12, fontWeight:700, color:T.text }}>{`KES ${parseFloat(b.amount).toLocaleString()}`}</p>
            <p style={{ fontSize:10, color:T.textDim }}>{ago(b.created_at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Sell Panel (shared desktop + mobile) ─────────────────────────────────────

const SellPanel = ({ market, token, T, sellAmount, setSellAmount, fetchMarket, updateUser, user, setPrediction, setSelectedOptionId, setAmount, setBetMode, setSheetOpen, setSuccessMsg, setError, isMobile }) => {
  const [confirmId, setConfirmId] = useState(null);
  if (!market?.user_positions?.length) return (
    <p style={{ color:T.textDim, fontSize:13, textAlign:'center', padding:'24px 0' }}>No active positions to sell</p>
  );

  return (
    <div>
      {market.user_positions.map((pos, i) => {
        const positionId = pos.bet_id || pos.id;
        const label = (pos.display_label || pos.option_label || pos.prediction)?.toUpperCase();
        const color = pos.prediction === 'yes' ? T.yes : pos.prediction === 'no' ? T.no : OPTION_COLORS[i % OPTION_COLORS.length];
        const stakedAmount = parseFloat(pos.total_amount || 0);
        const fullPayout = parseFloat(pos.sell_value || pos.potential_payout || 0);
        const selectedPct = sellAmount?.[positionId] ?? 100;
        const daysToClose = market?.closes_at
  ? (new Date(market.closes_at) - Date.now()) / (1000 * 60 * 60 * 24)
  : 0;
const exitFeeRate = daysToClose > 30 ? 0.20 : 0.30;
const receiveAmount = fullPayout * (selectedPct / 100) * (1 - 0.01 - exitFeeRate);
        const pnl = fullPayout - stakedAmount;
        const pnlPct = stakedAmount > 0 ? ((pnl / stakedAmount) * 100).toFixed(1) : '0';
        const isProfit = pnl >= 0;
        
        // ── If win now payout (share-based, matches backend resolution) ──
                const ifWinPayout = parseFloat(pos.potential_payout || 0) > 0
          ? parseFloat(pos.potential_payout)
          : null;



        return (
          <div key={i} style={{ marginBottom:20, paddingBottom:20, borderBottom: i < market.user_positions.length - 1 ? `1px solid ${T.border}` : 'none' }}>

            {/* Position header card */}
            <div style={{ padding:'12px 14px', borderRadius:8, background:`${color}18`, border:`1px solid ${color}33`, marginBottom:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <span style={{ fontSize:14, fontWeight:700, color }}>{label}</span>
                <span style={{ fontSize:12, fontWeight:700, color:isProfit?T.yes:T.no, background:isProfit?`${T.yes}18`:`${T.no}18`, padding:'3px 8px', borderRadius:20 }}>
                  {isProfit?'+':''}{pnlPct}%
                </span>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10 }}>
                <div>
                  <p style={{ fontSize:10, color:`${color}99`, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Bought</p>
                  <p style={{ fontSize:13, fontWeight:700, color:`${color}cc` }}>KES {stakedAmount.toLocaleString()}</p>
                </div>
                <div style={{ textAlign:'right' }}>
                  <p style={{ fontSize:10, color:`${color}99`, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>Current value</p>
                  <p style={{ fontSize:13, fontWeight:700, color:isProfit?T.yes:T.no }}>
                    KES {fullPayout.toFixed(2)}
                    <span style={{ fontSize:11, marginLeft:4 }}>{isProfit?'▲':'▼'} KES {Math.abs(pnl).toFixed(2)}</span>
                  </p>
                </div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', paddingTop:10, borderTop:`1px solid ${color}33` }}>
                
  {ifWinPayout !== null && (
  <div style={{ flex:1 }}>
    <p style={{ fontSize:10, color:`${color}99`, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:2 }}>If win now</p>
    <p style={{ fontSize:13, fontWeight:700, color:T.yes }}>KES {ifWinPayout.toFixed(2)}</p>
  </div>
)}
                <div style={{ flex:1, textAlign:'right', fontSize:11, color:T.textDim }}>
                    <p>2% res. fee</p>
                  <p>20% tax on gain</p>
                </div>
              </div>
            </div>

            {/* Shares */}
            <p style={{ fontSize:12, color:T.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:8 }}>Shares</p>
            <div style={{ display:'flex', gap:6, marginBottom:10 }}>
              {[25, 50, 100].map(pct => (
                <button key={pct}
                  onClick={() => setSellAmount(prev => ({
                    ...prev,
                    [positionId]: pct,
                    [`amt_${positionId}`]: ((stakedAmount * pct) / 100).toFixed(2),
                  }))}
                  style={{ flex:1, padding:'8px 0', borderRadius:6, border:`1px solid ${selectedPct===pct?T.accent:T.border}`, background:selectedPct===pct?`${T.accent}15`:T.surfaceHi, color:selectedPct===pct?T.accent:T.textMuted, fontWeight:600, cursor:'pointer', fontSize:12, fontFamily:'inherit', transition:'all 0.1s' }}>
                  {pct === 100 ? 'Max' : `${pct}%`}
                </button>
              ))}
            </div>

            {/* Amount input */}
            <input
              type="number"
              value={sellAmount?.[`amt_${positionId}`] ?? ''}
              onChange={e => {
                const val = Math.min(parseFloat(e.target.value) || 0, stakedAmount);
                const pct = stakedAmount > 0 ? (val / stakedAmount) * 100 : 100;
                setSellAmount(prev => ({ ...prev, [positionId]: pct, [`amt_${positionId}`]: e.target.value }));
              }}
              placeholder={`Max KES ${stakedAmount.toLocaleString()}`}
              min="1" max={stakedAmount}
              style={{ width:'100%', background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:7, padding:isMobile?13:12, color:T.text, fontSize:isMobile?15:14, marginBottom:10, fontFamily:'inherit', boxSizing:'border-box' }}
            />

            {/* Fee breakdown + You'll receive */}
            {(() => {
              const grossValue     = fullPayout * (selectedPct / 100);
              const daysToClose    = market?.closes_at
                ? (new Date(market.closes_at) - Date.now()) / (1000 * 60 * 60 * 24)
                : 999;
              const exitFeeRate  = pos.exit_fee_rate  ?? (daysToClose > 30 ? 0.20 : 0.30);
const exitFeeLabel = exitFeeRate === 0.20 ? '20%' : '30%';
const platformFeeAmt = grossValue * (pos.platform_fee ?? 0.01);
const poolReturnAmt  = grossValue * exitFeeRate;
const netValue       = grossValue * (1 - (pos.platform_fee ?? 0.01) - exitFeeRate);
              return (
                <div style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px', marginBottom:12 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:12, color:T.textDim }}>Pool share</span>
                    <span style={{ fontSize:12, color:T.textMuted }}>KES {grossValue.toFixed(2)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                    <span style={{ fontSize:12, color:T.textDim }}>Platform fee (1%)</span>
                    <span style={{ fontSize:12, color:T.no }}>− KES {platformFeeAmt.toFixed(2)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between', marginBottom:10, paddingBottom:10, borderBottom:`1px solid ${T.border}` }}>
                    <span style={{ fontSize:12, color:T.textDim }}>Exit fee ({exitFeeLabel} → pool)</span>
                    <span style={{ fontSize:12, color:T.no }}>− KES {poolReturnAmt.toFixed(2)}</span>
                  </div>
                  <div style={{ display:'flex', justifyContent:'space-between' }}>
                    <span style={{ fontSize:13, fontWeight:700, color:T.text }}>You'll receive</span>
                    <span style={{ fontSize:15, fontWeight:800, color:T.yes }}>KES {netValue.toFixed(2)}</span>
                  </div>
                  {selectedPct < 100 && (
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
                      <span style={{ fontSize:11, color:T.textDim }}>Remaining value</span>
                      <span style={{ fontSize:11, color:T.textMuted }}>KES {(fullPayout * (1 - selectedPct / 100) * (1 - 0.01 - exitFeeRate)).toFixed(2)}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Sell button */}
            
            {confirmId === positionId ? (
  <div style={{ background: `${T.no}12`, border: `1px solid ${T.no}33`, borderRadius: 8, padding: '12px 14px' }}>
    <p style={{ color: T.no, fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Confirm sell?</p>
    <div style={{ display: 'flex', gap: 8 }}>
      <button
        onClick={async () => {
          if (!positionId) { setError('Cannot identify position.'); return; }
          try {
            const res = await axios.post(
              `${API}/api/bets/sell/${positionId}`,
              { fraction: selectedPct / 100 },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const actualPayout = res.data?.sell_value ?? receiveAmount;
            await fetchMarket();
            const profileRes = await axios.get(`${API}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } });
            updateUser({ balance: profileRes.data.balance });
            setPrediction(''); setSelectedOptionId(null); setAmount(''); setSellAmount({});
            setBetMode('buy');
            if (setSheetOpen) setSheetOpen(false);
            setSuccessMsg(`Sold ${label} · received KES ${actualPayout.toFixed(2)}`);
            setConfirmId(null);
            setTimeout(() => setSuccessMsg(''), 4000);
          } catch (err) { setError(err.response?.data?.message || 'Failed to sell position'); }
        }}
        style={{ flex: 1, background: T.accent, color: '#fff', border: 'none', borderRadius: 7, padding: '10px 0', fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
        Confirm
      </button>
      <button onClick={() => setConfirmId(null)}
        style={{ flex: 1, background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 7, padding: '10px 0', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' }}>
        Cancel
      </button>
    </div>
  </div>
) : (
  <button onClick={() => setConfirmId(positionId)}
    style={{ width: '100%', padding: isMobile ? 16 : 14, borderRadius: isMobile ? 10 : 8, border: 'none', background: T.accent, color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: isMobile ? 16 : 15, fontFamily: 'inherit' }}>
    Sell {selectedPct === 100 ? 'Max' : `${selectedPct}%`}
  </button>
)}
             
          </div>
        );
      })}
    </div>
  );
};

const StakeWarning = ({ estimate, amount, T, setAmount }) => {
  if (!estimate?.stake_warning || !(parseFloat(amount) > 0)) return null;
  const max = estimate.max_useful_stake;
  return (
    <div style={{ background:`${T.gold}12`, border:`1px solid ${T.gold}44`, borderRadius:8, padding:'10px 12px', marginBottom:12 }}>
      <p style={{ fontSize:12, fontWeight:700, color:T.gold, marginBottom:4 }}>⚠ Your profit is limited at this stake</p>
      <p style={{ fontSize:12, color:T.textMuted, lineHeight:1.5 }}>{estimate.stake_warning}</p>
      {max && parseFloat(amount) > max && (
        <button type="button" onClick={() => setAmount(String(max))}
          style={{ marginTop:8, background:'transparent', color:T.gold, border:`1px solid ${T.gold}55`, borderRadius:6, padding:'5px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
          Use KES {max.toLocaleString()} instead
        </button>
      )}
    </div>
  );
};

// ─── Main Market Page ─────────────────────────────────────────────────────────
export default function MarketPage() {


  const { id }       = useParams();
  const navigate     = useNavigate();
  const { token, user, updateUser, logout } = useAuth();
  const { T }        = useTheme();
  const isMobile     = useMobile();
  const location     = useLocation();
  const chamaContext = location.state?.chamaMode ? location.state : null;
  const [chamaPoolBalance, setChamaPoolBalance] = useState(chamaContext?.chamaBalance || null);

  const [market, setMarket]                    = useState(null);
  const [priceHistory, setPriceHistory]         = useState([]);
  const [prediction, setPrediction]             = useState('');
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [amount, setAmount]                     = useState('');
  const [error, setError]                       = useState('');
  const [betLoading, setBetLoading]             = useState(false);
  const [successMsg, setSuccessMsg]             = useState('');
  const [loading, setLoading]                   = useState(true);
  const [copied, setCopied]                     = useState(false);
  const [imgError, setImgError]                 = useState(false);
  const [bottomTab, setBottomTab]               = useState('comments');
  const [sheetOpen, setSheetOpen]               = useState(false);
  const [betMode, setBetMode]                   = useState('buy');
  const [sellAmount, setSellAmount]             = useState({});
  const [bookmarked, setBookmarked]             = useState(false);
  const [bookmarkLoading, setBookmarkLoading]   = useState(false);
  const sseRef = useRef(null);
  const refetchTimer = useRef(null);

  // ── Helpers ──
  const daysLeft = mkt => {
    if (!mkt?.closes_at) return null;
    const d = Math.ceil((new Date(mkt.closes_at) - Date.now()) / 86400000);
    if (d < 0) return 'Ended';
    if (d === 0) return 'Ends today';
    if (d === 1) return '1 day left';
    return `${d} days left`;
  };

  const getProbSummary = () => {
    const rows = Array.isArray(priceHistory) ? priceHistory : (priceHistory?.data || []);
    if (!rows.length) return null;
    const now = Date.now();
    const week = rows.filter(r => now - new Date(r.recorded_at).getTime() <= 7*86400000);
    if (week.length < 2) return null;
    const oldest = parseFloat(week[0].yes_percent ?? week[0].percent ?? 0);
    const newest = parseFloat(week[week.length-1].yes_percent ?? week[week.length-1].percent ?? 0);
    const diff = newest - oldest;
    return { oldest: oldest.toFixed(0), diff: diff.toFixed(1), up: diff >= 0 };
  };

 useEffect(() => { fetchMarket(); fetchPriceHistory(); }, [id]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => updateUser({ balance: r.data.balance })).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/markets/${id}/bookmark`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setBookmarked(r.data.bookmarked))
      .catch(() => {});
  }, [id, token]);

  useEffect(() => {
    if (market?.market_type==='multiple' && market.options?.length && !prediction) {
      setPrediction(market.options[0].label);
      setSelectedOptionId(market.options[0].id);
    }
  }, [market]);

  useEffect(() => {
    const es = new EventSource(`${API}/api/bets/live-feed?market_id=${id}`);
    sseRef.current = es;
    es.onmessage = e => {
      try {
        JSON.parse(e.data); // validate payload before refetching
      } catch (_) { return; }
      clearTimeout(refetchTimer.current);
      refetchTimer.current = setTimeout(() => {
        fetchMarket();
        fetchPriceHistory();
      }, 400);
    };
    es.onerror = () => {
      es.close();
      
    };
    return () => { clearTimeout(refetchTimer.current); sseRef.current?.close(); };
  }, [id]);

    const fetchMarket = React.useCallback(async () => {
        try {
       const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [marketRes, betsRes] = await Promise.all([
      axios.get(`${API}/api/markets/${id}`, { headers, timeout: 10000 }),
      token ? axios.get(`${API}/api/bets/my-bets`, { headers, timeout: 10000 }) : Promise.resolve({ data: [] }),
    ]);

    const market = marketRes.data;

    // Overwrite potential_payout on user_positions with correct value from getUserBets
    if (market.user_positions?.length && betsRes.data?.length) {
  market.user_positions = market.user_positions.map(pos => {
    const match = betsRes.data.find(b => b.id === pos.bet_id);
    if (match) pos.potential_payout = match.potential_payout;
    return pos;
  });
}

    setMarket(market);
    if (market.market_type === 'multiple' && market.options?.length && !selectedOptionId) {
      setPrediction(market.options[0].label);
      setSelectedOptionId(market.options[0].id);
    }
} catch (err) {
    if (err?.response?.status === 401) { logout(); return; }
    if (err?.response?.status === 404) navigate('/');
  }
  finally { setLoading(false); }
}, [id, token]);

  const fetchPriceHistory = React.useCallback(async () => {
    try { const r = await axios.get(`${API}/api/markets/${id}/price-history`); setPriceHistory(r.data); } catch (_) {}
  }, [id]);

  const getProbs = () => {
    if (!market) return [];
    if (market.market_type==='multiple' && market.options?.length) {
      const rawRows = priceHistory?.data || [];
return market.options.map((o,i) => {
  const optRows = rawRows.filter(r => String(r.option_id) === String(o.id));
  const latestPct = optRows.length ? parseFloat(optRows[optRows.length - 1].percent) : null;
  const totalPool = market.options.reduce((s,op) => s + parseFloat(op.total_amount||0), 0);
  return {
    ...o,
    prob: latestPct !== null
      ? Math.round(latestPct)
      : totalPool > 0
        ? Math.round((parseFloat(o.total_amount||0)/totalPool)*100)
        : Math.round(100/market.options.length),
    color: OPTION_COLORS[i%OPTION_COLORS.length]
  };
});
    }
    const y=parseFloat(market.total_yes||0), n=parseFloat(market.total_no||0), t=y+n;
const yp = market.yes_price
  ? Math.round(parseFloat(market.yes_price) * 100)
  : t > 0 ? Math.round((y/t)*100) : 50;
    return [{ label:'YES', prob:yp, color:T.yes, total_amount:y }, { label:'NO', prob:100-yp, color:T.no, total_amount:n }];
  };

   const [estimate, setEstimate] = useState({ payout: 0, profit: 0, shares: 0, price: 0.5 });

  useEffect(() => {
    const stake = parseFloat(amount) || 0;
    const ready = market && stake > 0 &&
      (market.market_type === 'multiple' ? selectedOptionId : (prediction === 'yes' || prediction === 'no'));
    if (!ready) { setEstimate({ payout: 0, profit: 0, shares: 0, price: 0.5 }); return; }

    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/markets/${id}/estimate`, {
          params: { stake, side: prediction, option_id: selectedOptionId },
        });
        if (!cancelled) setEstimate(r.data);
      } catch (_) {}
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [amount, prediction, selectedOptionId, market?.id, market?.total_yes, market?.total_no]);

  const calcPayout = () => estimate;
const handleBet = async e => {
    e?.preventDefault(); setError('');
    if (!token) { navigate('/login'); return; }
    setBetLoading(true);
    try {
      if (chamaContext) {
        // Vote-gated check
        if (chamaContext.voteBeforeTrade) {
          setError('This chama requires a vote before trading. Go to Votes to propose this trade.');
          setBetLoading(false);
          return;
        }
        // Place as chama trade
        await axios.post(
          `${API}/api/chamas/${chamaContext.chamaId}/trades`,
          { market_id: market.id, outcome: prediction?.toUpperCase(), amount: parseFloat(amount), option_id: selectedOptionId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Refresh chama pool balance
        const chamaRes = await axios.get(`${API}/api/chamas/${chamaContext.chamaId}`, { headers: { Authorization: `Bearer ${token}` } });
        setChamaPoolBalance(parseFloat(chamaRes.data.chama.wallet_balance));
        setSuccessMsg(`${chamaContext.chamaName} traded KES ${parseFloat(amount).toLocaleString()} on ${prediction.toUpperCase()}`);
      } else {
        // Normal personal trade
        await axios.post(
          `${API}/api/bets`,
          { market_id: market.id, prediction, amount: parseFloat(amount), option_id: selectedOptionId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const profileRes = await axios.get(`${API}/api/users/profile`, { headers: { Authorization: `Bearer ${token}` } });
        updateUser({ balance: profileRes.data.balance });
        setSuccessMsg(`Trade placed — KES ${parseFloat(amount).toLocaleString()} on ${prediction.toUpperCase()}`);
      }
      setPrediction(''); setSelectedOptionId(null); setAmount('');
      fetchMarket(); fetchPriceHistory();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) { setError(err.response?.data?.message || err.response?.data?.error || 'Failed to place trade'); }
    finally { setBetLoading(false); }
  };

  const handleShare = async () => {
  const marketUrl = `https://minomarkets.app/markets/${id}`;
  if (navigator.share) {
    try { await navigator.share({ title: market?.title, url: marketUrl }); } catch(_) {}
  } else {
    await navigator.clipboard.writeText(marketUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
};

 if (loading) return <MarketPageSkeleton T={T} isMobile={isMobile} />;
  if (!market) return null;

  const probs      = getProbs();
  const isMultiple = market.market_type === 'multiple';
  const isClosed   = market.status !== 'open';
  const yes        = parseFloat(market.total_yes||0);
  const no         = parseFloat(market.total_no ||0);
  const total      = yes + no;
  const yesProb    = probs[0]?.prob ?? 50;
  const { payout, profit } = calcPayout();
  const QUICK      = [50, 100, 500, 1000];
  const hasImg     = market.image_url && !imgError;
  const catCode    = (market.category||'MKT').slice(0,3).toUpperCase();
  const selOption  = isMultiple ? probs.find(p => p.label===prediction) : null;

  return (
    <>
      <Helmet>
  <title>{market?.title ? `${market.title} | Mino Markets` : 'Mino Markets'}</title>
  <meta name="description" content={market?.description || `Trade on "${market?.title}" — a prediction market on Mino Markets.`} />
  
  {/* Open Graph */}
  <meta property="og:type" content="website" />
  <meta property="og:site_name" content="Mino Markets" />
  <meta property="og:title" content={market?.title || 'Mino Markets'} />
  <meta property="og:description" content={market?.description || `Trade on this market on Mino Markets.`} />
  <meta property="og:url" content={`https://minomarkets.app/markets/${id}`} />
  <meta property="og:image" content={market?.image_url || 'https://minomarkets.app/og-default.png'} />
  <meta property="og:image:width" content="1200" />
  <meta property="og:image:height" content="630" />

  {/* Twitter/X */}
  <meta name="twitter:card" content="summary_large_image" />
  <meta name="twitter:title" content={market?.title || 'Mino Markets'} />
  <meta name="twitter:description" content={market?.description || `Trade on this market on Mino Markets.`} />
  <meta name="twitter:image" content={market?.image_url || 'https://minomarkets.app/og-default.png'} />
</Helmet>
      <div style={{ minHeight:'100vh', background:T.bg, fontFamily:"-apple-system,'Segoe UI',Roboto,sans-serif", color:T.text, paddingBottom:isMobile?120:0 }}>
        <style>{`
          @keyframes fadeUp  { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
          @keyframes slideUp { from{transform:translateY(100%)} to{transform:translateY(0)} }
          @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.4} }
          @keyframes drawLine { from{stroke-dashoffset:1} to{stroke-dashoffset:0} }
          @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
          * { box-sizing:border-box; margin:0; padding:0; }
          ::-webkit-scrollbar{width:2px} ::-webkit-scrollbar-thumb{background:${T.border};border-radius:2px}
          input:focus{border-color:${T.accent}!important;outline:none}
          input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
          .cat-nav::-webkit-scrollbar{display:none}
        `}</style>

        {/* Nav */}
        <nav style={{ background:`${T.bg}f2`, backdropFilter:'blur(16px)', borderBottom:`1px solid ${T.border}`, position:'sticky', top:0, zIndex:100 }}>
          {/* Top bar */}
          <div style={{ height:56, display:'flex', alignItems:'center', justifyContent:'space-between', padding:'0 20px' }}>
            <div onClick={() => navigate('/markets')} style={{ cursor:'pointer', display:'flex', alignItems:'center' }}>
              <svg width="100" height="32" viewBox="0 0 120 38" fill="none">
                <rect x="1" y="1" width="36" height="36" rx="9" fill={T.surface} stroke={T.border} strokeWidth="1.5"/>
                <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="3" strokeLinecap="round"/>
                <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="3" strokeLinecap="round"/>
                <text x="46" y="20" dominantBaseline="middle" fontFamily="inherit" fontWeight="800" fontSize="22" fill={T.text} letterSpacing="-1">mino</text>
              </svg>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {/* Search bar — desktop only */}
              {!isMobile && (
                <div onClick={() => navigate('/markets')} style={{ display:'flex', alignItems:'center', gap:8, background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8, padding:'7px 14px', cursor:'pointer', width:220 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={T.textDim} strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                  <span style={{ fontSize:13, color:T.textDim }}>Search markets...</span>
                </div>
              )}
              <button onClick={() => navigate('/markets')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>Markets</button>
              {token && !isMobile && <button onClick={() => navigate('/dashboard')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:7, padding:'6px 14px', cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>Dashboard</button>}
            </div>
          </div>
          {/* Category nav bar */}
          <div className="cat-nav" style={{ display:'flex', overflowX:'auto', padding:'0 20px', gap:4, paddingBottom:1, scrollbarWidth:'none' }}>
            {['All','Politics','Football','Economics','Entertainment','Business','Events','Sports','Technology','Health','News','Global'].map(cat => {
              const isActive = cat === 'All' ? false : market?.category?.toLowerCase() === cat.toLowerCase();
              return (
                <button key={cat}
                  onClick={() => navigate(cat === 'All' ? '/markets' : `/markets?category=${cat.toLowerCase()}`)}
                  style={{ padding:'8px 16px', background:'transparent', border:'none', borderBottom:`2px solid ${isActive ? T.accent : 'transparent'}`, color: isActive ? T.text : T.textMuted, fontWeight: isActive ? 700 : 400, cursor:'pointer', fontSize:13, fontFamily:'inherit', whiteSpace:'nowrap', transition:'all 0.1s', flexShrink:0 }}>
                  {cat}
                </button>
              );
            })}
          </div>
        </nav>
       


        {chamaContext && (
  <div style={{
    background: `${T.accent}14`, borderBottom: `1px solid ${T.accent}33`,
    padding: '10px 20px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontSize: 16 }}>👤👤👤</span>
      <span style={{ color: T.accent, fontSize: 13, fontWeight: 600 }}>
        Trading as <strong>{chamaContext.chamaName}</strong>
      </span>
      {chamaContext.voteBeforeTrade && (
        <span style={{
          fontSize: 10, background: `${T.gold}20`, color: T.gold,
          border: `1px solid ${T.gold}44`, borderRadius: 4,
          padding: '2px 8px', fontWeight: 600,
        }}>VOTE-GATED</span>
      )}
    </div>
    <button onClick={() => navigate(`/markets/${id}`, { state: {} })} style={{
      background: 'transparent', border: `1px solid ${T.border}`,
      borderRadius: 6, color: T.textMuted, fontSize: 11,
      padding: '4px 10px', cursor: 'pointer', fontFamily: 'inherit',
    }}>Exit chama mode</button>
  </div>
)}
{/* Market Header */}
<div style={{ borderBottom:`1px solid ${T.border}`, padding:isMobile?'16px':'20px 28px', background:T.bg }}>
          <div style={{ maxWidth:1200, margin:'0 auto' }}>
            {/* Breadcrumb + badges */}
  <p style={{ fontSize:13, color:T.textMuted, marginBottom:8, display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
              <span style={{ cursor:'pointer' }} onClick={() => navigate('/markets')}>{market.category}</span>
              {daysLeft(market) && (
                <span style={{ fontSize:11, color: daysLeft(market)==='Ends today'?T.no:T.textDim, background:T.surfaceHi, padding:'2px 8px', borderRadius:20, border:`1px solid ${T.border}` }}>
                  {daysLeft(market)}
                </span>
              )}
            </p>

            <div style={{ display:'flex', gap:16, alignItems:'flex-start' }}>
              {/* Image — slightly larger */}
              <div style={{ width:isMobile?56:72, height:isMobile?56:72, borderRadius:10, overflow:'hidden', flexShrink:0, background:T.surface, border:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {hasImg
                  ? <img src={market.image_url} alt="" onError={() => setImgError(true)} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                  : <span style={{ fontSize:11, fontWeight:800, color:T.textDim }}>{catCode}</span>
                }
              </div>

              <div style={{ flex:1, minWidth:0 }}>
                {/* Title + bookmark */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                  <h1 style={{ fontSize:isMobile?18:24, fontWeight:700, letterSpacing:'-0.5px', lineHeight:1.3, marginBottom:10 }}>{market.title}</h1>
                  <button
                    onClick={async () => {
                      if (!token) { navigate('/login'); return; }
                      setBookmarkLoading(true);
                      try {
                        const r = await axios.post(
                          `${API}/api/markets/${id}/bookmark`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setBookmarked(r.data.bookmarked);
                        setSuccessMsg(r.data.message);
                        setTimeout(() => setSuccessMsg(''), 2000);
                      } catch (_) {}
                      setBookmarkLoading(false);
                    }}
                    disabled={bookmarkLoading}
                    title={bookmarked ? 'Remove bookmark' : 'Save market'}
                    style={{ background:bookmarked?`${T.accent}15`:'transparent', border:`1px solid ${bookmarked?T.accent:T.border}`, borderRadius:7, cursor:bookmarkLoading?'not-allowed':'pointer', color:bookmarked?T.accent:T.textMuted, flexShrink:0, padding:'6px 8px', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s', opacity:bookmarkLoading?0.5:1 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={bookmarked?'currentColor':'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                    </svg>
                  </button>
                </div>

                {/* Big probability + trend + history summary */}
                {!isMultiple && (
                  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10, flexWrap:'wrap' }}>
                    <span style={{ fontSize:isMobile?26:34, fontWeight:800, color:yesProb>50?T.yes:T.no, letterSpacing:'-1px' }}>
                      {yesProb>50?yesProb:100-yesProb}%
                    </span>
                    <span style={{ fontSize:14, color:T.textMuted }}>chance</span>
                    {(market.yes_label && market.yes_label !== 'Yes') || (market.no_label && market.no_label !== 'No') ? (
                      <span style={{ fontSize:13, fontWeight:700, color:yesProb>50?T.yes:T.no, background:yesProb>50?T.yesDim:T.noDim, padding:'3px 10px', borderRadius:20 }}>
                        {yesProb>50?(market.yes_label||'Yes'):(market.no_label||'No')} leading
                      </span>
                    ) : (
                      <span style={{ fontSize:13, fontWeight:700, color:yesProb>50?T.yes:T.no }}>{yesProb>50?'YES':'NO'}</span>
                    )}
                    {/* Probability history summary */}
                    {(() => {
                      const s = getProbSummary();
                      if (!s) return null;
                      return (
                        <span style={{ fontSize:11, color:T.textDim }}>
                          Was {s.oldest}% · <span style={{ color:s.up?T.yes:T.no, fontWeight:600 }}>{s.up?'▲':'▼'} {Math.abs(s.diff)}% this week</span>
                        </span>
                      );
                    })()}
                  </div>
                )}

                {/* Meta row */}
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center' }}>
                  <span style={{ fontSize:11, color:T.textDim }}>Vol {total >= 10000 ? fmt(total) : '<10K'}</span>
                  <span style={{ fontSize:11, color:T.textDim }}>·</span>
                
                  <span style={{ fontSize:11, color:T.textDim }}>·</span>
                  <span style={{ fontSize:11, color:T.textDim }}>Closes {new Date(market.closes_at).toLocaleDateString()}</span>
                  <button onClick={handleShare} style={{ background:'transparent', border:`1px solid ${T.border}`, borderRadius:5, padding:'3px 10px', cursor:'pointer', color:copied?T.yes:T.textMuted, fontSize:11, fontFamily:'inherit' }}>
                    {copied ? '✓ Copied' : 'Share'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ maxWidth:1200, margin:'0 auto', padding:isMobile?'16px':'20px 28px', animation:'fadeUp 0.3s ease' }}>
          <div style={{ display:'grid', gridTemplateColumns:isMobile?'1fr':'1fr 320px', gap:20, alignItems:'start' }}>

            {/* ── Left column ── */}
            <div>

              {/* Binary chart */}
              {!isMultiple && (
                <div style={{ marginBottom: 32 }}>
                  <PriceChart data={priceHistory} selectedOptionId={selectedOptionId} T={T} market={market} />
                </div>
              )}

              {/* Multi-choice outcomes */}
              {isMultiple && (
                <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden', marginBottom: 24 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '1fr 120px auto', gap: 12, padding: '10px 20px', borderBottom: `1px solid ${T.border}` }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.07em' }}>Outcome</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: T.textDim, textTransform: 'uppercase', letterSpacing: '0.07em', textAlign: 'right' }}>Chance</span>
                    {!isClosed && !isMobile && <span/>}
                  </div>
                  {probs.map(p => (
                    <React.Fragment key={p.id || p.label}>
                      <div
                        onClick={() => { setPrediction(prediction === p.label ? '' : p.label); setSelectedOptionId(prediction === p.label ? null : p.id); if(isMobile) setSheetOpen(true); }}
                        style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr auto' : '1fr 120px auto', gap: 12, alignItems: 'center', padding: isMobile ? '12px 14px' : '14px 20px', borderBottom: prediction === p.label ? 'none' : `1px solid ${T.border}`, cursor: 'pointer', background: prediction === p.label ? `${p.color}08` : 'transparent', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = prediction === p.label ? `${p.color}10` : T.surfaceHi}
                        onMouseLeave={e => e.currentTarget.style.background = prediction === p.label ? `${p.color}08` : 'transparent'}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          {p.image_url
                            ? <img src={p.image_url} alt={p.label} style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover', flexShrink: 0, border: prediction === p.label ? `2px solid ${p.color}` : '2px solid transparent' }} onError={e => e.target.style.display='none'}/>
                            : <div style={{ width: 40, height: 40, borderRadius: 8, background: `${p.color}15`, border: `2px solid ${prediction === p.label ? p.color : p.color + '22'}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 10, height: 10, borderRadius: '50%', background: p.color }}/></div>
                          }
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 15, color: prediction === p.label ? p.color : T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</div>
                            <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>{fmt(p.total_amount || 0)} Vol.</div>
                          </div>
                        </div>
                        <span style={{ color: T.text, fontWeight: 800, fontSize: 24, textAlign: 'right', letterSpacing: '-1px' }}>{p.prob}%</span>
                        {!isClosed && !isMobile && (
                          <button onClick={e => { e.stopPropagation(); setPrediction(prediction === p.label ? '' : p.label); setSelectedOptionId(prediction === p.label ? null : p.id); }}
                            style={{ padding: '9px 18px', borderRadius: 6, border: 'none', background: prediction === p.label ? '#0d8f5c' : T.yes, color: prediction === p.label ? '#fff' : '#0f1011', fontWeight: 700, cursor: 'pointer', fontSize: 13, fontFamily: 'inherit', transition: 'all 0.12s' }}>
                            Buy {p.prob}%
                          </button>
                        )}
                      </div>

                      {prediction === p.label && (
                        <div style={{ borderBottom: `1px solid ${T.border}` }}>
                          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}` }}>
                            {successMsg && <div style={{ background: T.yesDim, border: `1px solid ${T.yes}33`, color: T.yes, padding: '10px 12px', borderRadius: 7, marginBottom: 12, fontSize: 13 }}>{successMsg}</div>}
                            {error && <div style={{ background: T.noDim, border: `1px solid ${T.no}33`, color: T.no, padding: '10px 12px', borderRadius: 7, marginBottom: 12, fontSize: 13 }}>{error}</div>}
                            <div style={{ marginBottom: 12, padding: '10px 12px', borderRadius: 8, background: `${p.color}12`, border: `1px solid ${p.color}33`, textAlign: 'center', fontSize: 14, fontWeight: 700, color: p.color }}>
                              Buy {p.label} · {p.prob}% chance {payout > 0 ? `· Payout KES ${payout.toFixed(2)}` : ''}
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                              {QUICK.map(a => (
                                <button key={a} onClick={e => { e.stopPropagation(); setAmount(a.toString()); }}
                                  style={{ flex: 1, padding: '8px 4px', borderRadius: 6, border: `1px solid ${amount === a.toString() ? T.accent : T.border}`, background: amount === a.toString() ? `${T.accent}15` : T.surfaceHi, color: amount === a.toString() ? T.accent : T.textMuted, fontWeight: 600, cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                                  +{a >= 1000 ? `${a/1000}K` : a}
                                </button>
                              ))}
                            </div>
                            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount (KES)" min="1"
                              onClick={e => e.stopPropagation()}
                              style={{ width: '100%', background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 7, padding: 12, color: T.text, fontSize: 14, marginBottom: 10, fontFamily: 'inherit', boxSizing: 'border-box' }}/>
                            {amount && parseFloat(amount) > 0 && (
                              <div style={{ background: T.surfaceHi, border: `1px solid ${T.border}`, borderRadius: 7, padding: '10px 14px', marginBottom: 10, display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ fontSize: 12, color: T.textMuted }}>Est. return if correct now</span>
                                <span style={{ fontSize: 12, color: T.yes, fontWeight: 700 }}>KES {payout.toFixed(2)}</span>
                              </div>
                            )}
                                                        <StakeWarning estimate={estimate} amount={amount} T={T} setAmount={setAmount}/>
                            <button onClick={e => { e.stopPropagation(); handleBet(); }} disabled={!amount || betLoading}
                              style={{ width: '100%', background: !amount ? T.surfaceHi : p.color, color: !amount ? T.textDim : p.color === T.yes ? '#0f1011' : '#fff', border: 'none', borderRadius: 8, padding: 13, fontWeight: 700, cursor: !amount ? 'not-allowed' : 'pointer', fontSize: 14, fontFamily: 'inherit' }}>
                              {betLoading ? 'Trading...' : `Trade ${p.label}`}
                            </button>
                          </div>
                          <SelectedOptionChart data={priceHistory} selectedOptionId={selectedOptionId} selOption={p} T={T}/>
                        </div>
                      )}
                    </React.Fragment>
                  ))}

                  <div style={{ padding: 20, borderTop: `1px solid ${T.border}` }}>
                    <PriceChart data={priceHistory} selectedOptionId={selectedOptionId} T={T}/>
                  </div>
                </div>
              )}

               {market.description && (
  <p style={{ color:T.textMuted, fontSize:14, lineHeight:1.8, whiteSpace:'pre-wrap', marginBottom: market.rules ? 12 : 0 }}>
    {market.description}
  </p>
)}

              {/* Resolution Rules */}
              <div style={{ marginBottom:24, paddingBottom:24, borderBottom:`1px solid ${T.border}` }}>
                <p style={{ fontSize:12, fontWeight:600, color:T.textMuted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:12 }}>Resolution Rules</p>
               
{market.rules
  ? <p style={{ color:T.textMuted, fontSize:14, lineHeight:1.8, whiteSpace:'pre-wrap' }}>{market.rules}</p>
  : !market.description && <p style={{ color:T.textDim, fontSize:13 }}>No resolution rules provided.</p>
}
                <div style={{ display:'flex', gap:24, flexWrap:'wrap', marginTop:16 }}>
                  {[
                    { label:'Closes', value:new Date(market.closes_at).toLocaleString() },
                    { label:'Category', value:market.category },
                    { label:'Total Volume', value: total >= 10000 ? fmt(total) : '<10K' },
                    ...(market.status==='resolved'?[{ label:'Outcome', value:market.outcome?.toUpperCase() }]:[]),
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <p style={{ color:T.textDim, fontSize:11, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>{label}</p>
                      <p style={{ color:T.text, fontSize:13 }}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Comments / Activity tabs */}
              <div>
                <div style={{ display:'flex', gap:0, borderBottom:`1px solid ${T.border}`, marginBottom:20 }}>
                  {[['comments','Comments'],['activity','Activity']].map(([key,label]) => (
                    <button key={key} onClick={() => setBottomTab(key)}
                      style={{ padding:'10px 20px', background:'transparent', border:'none', borderBottom:`2px solid ${bottomTab===key?T.accent:'transparent'}`, color:bottomTab===key?T.text:T.textMuted, fontWeight:bottomTab===key?600:400, cursor:'pointer', fontSize:13, fontFamily:'inherit', marginBottom:-1, transition:'all 0.1s' }}>
                      {label}
                    </button>
                  ))}
                </div>
                {bottomTab==='comments' && <Comments marketId={id} token={token} navigate={navigate} T={T}/>}
                {bottomTab==='activity' && <ActivityFeed marketId={id} market={market} T={T}/>}
              </div>
            </div>

            {/* ── Right panel ── */}
            <div style={{ position:'sticky', top:96, maxHeight:'calc(100vh - 112px)', overflowY:'auto', display:isMobile?'none':'block' }}>
              {isClosed ? (
                <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:24, textAlign:'center' }}>
                  <p style={{ fontWeight:700, color:T.text, marginBottom:6, fontSize:15 }}>Market {market.status}</p>
                  <p style={{ color:T.textMuted, fontSize:13 }}>{market.status==='resolved'?`Resolved: ${market.outcome?.toUpperCase()}`:'Trading is closed'}</p>
                </div>
              ) : !token ? (
                <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, padding:24 }}>
                  <p style={{ fontWeight:700, color:T.text, marginBottom:6, fontSize:15 }}>Predict this market</p>
                  <p style={{ color:T.textMuted, fontSize:13, marginBottom:20 }}>Sign in to place your prediction</p>
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <button onClick={() => navigate('/register')} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:12, fontWeight:700, cursor:'pointer', fontSize:14, fontFamily:'inherit' }}>Create account</button>
                    <button onClick={() => navigate('/login')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:12, cursor:'pointer', fontSize:14, fontFamily:'inherit' }}>Sign in</button>
                  </div>
                </div>
              ) : (
                <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10, overflow:'hidden' }}>

                  {/* Buy/Sell header */}
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderBottom:`1px solid ${T.border}` }}>
                    <div style={{ display:'flex', gap:0 }}>
                      {['buy','sell'].map(tab => (
                        <button key={tab} onClick={() => setBetMode(tab)}
                          style={{ background:'transparent', border:'none', borderBottom:`2px solid ${betMode===tab?T.accent:'transparent'}`, color:betMode===tab?T.text:T.textMuted, fontWeight:betMode===tab?700:400, cursor:'pointer', fontSize:14, padding:'4px 16px 8px', fontFamily:'inherit', textTransform:'capitalize', transition:'all 0.1s', position:'relative', zIndex:1 }}>
                          {tab.charAt(0).toUpperCase()+tab.slice(1)}
                        </button>
                      ))}
                    </div>
                    <div style={{ textAlign:'right' }}>
  <p style={{ fontSize:10, color:T.textDim, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:2 }}>
    {chamaContext ? chamaContext.chamaName : 'Balance'}
  </p>
  <p style={{ fontSize:13, fontWeight:800, color: chamaContext ? '#a78bfa' : T.yes }}>
    {chamaContext
      ? `KES ${(chamaPoolBalance || 0).toLocaleString()}`
      : `KES ${parseFloat(user?.balance || 0).toLocaleString()}`
    }
  </p>
  {chamaContext && (
    <p style={{ fontSize:10, color:T.textDim, marginTop:2 }}>pool balance</p>
  )}
</div>
                  </div>

                  <div style={{ padding:16 }}>
                    {betMode === 'sell' ? (
                      <SellPanel market={market} token={token} T={T} sellAmount={sellAmount} setSellAmount={setSellAmount} fetchMarket={fetchMarket} updateUser={updateUser} user={user} setPrediction={setPrediction} setSelectedOptionId={setSelectedOptionId} setAmount={setAmount} setBetMode={setBetMode} setSheetOpen={null} setSuccessMsg={setSuccessMsg} setError={setError} isMobile={false}/>
                    ) : (
                      <>
                        {successMsg && <div style={{ background:T.yesDim, border:`1px solid ${T.yes}33`, color:T.yes, padding:'10px 12px', borderRadius:7, marginBottom:12, fontSize:13 }}>{successMsg}</div>}
                        {error      && <div style={{ background:T.noDim,  border:`1px solid ${T.no}33`,  color:T.no,  padding:'10px 12px', borderRadius:7, marginBottom:12, fontSize:13 }}>{error}</div>}

                        {!isMultiple && (
                          <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                            <button onClick={() => setPrediction(prediction==='yes'?'':'yes')}
                              style={{ flex:1, padding:'12px 10px', borderRadius:8, border:'none', background:prediction==='yes'?T.yes:T.yesDim, color:prediction==='yes'?'#0f1011':T.yes, fontWeight:800, cursor:'pointer', fontSize:15, fontFamily:'inherit', transition:'all 0.12s' }}>
                              {market.yes_label||'Yes'} {yesProb}%
                            </button>
                            <button onClick={() => setPrediction(prediction==='no'?'':'no')}
                              style={{ flex:1, padding:'12px 10px', borderRadius:8, border:'none', background:prediction==='no'?T.no:T.noDim, color:prediction==='no'?'#fff':T.no, fontWeight:800, cursor:'pointer', fontSize:15, fontFamily:'inherit', transition:'all 0.12s' }}>
                              {market.no_label||'No'} {100-yesProb}%
                            </button>
                          </div>
                        )}

                        {isMultiple && selOption && (
                          <div style={{ marginBottom:16, padding:'10px 14px', borderRadius:8, background:`${selOption.color}15`, border:`1px solid ${selOption.color}33`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <span style={{ fontSize:14, fontWeight:700, color:selOption.color }}>{prediction}</span>
                            <span style={{ fontSize:13, color:selOption.color }}>{selOption.prob}%</span>
                          </div>
                        )}

                        {((!isMultiple&&prediction)||(isMultiple&&selOption)) && (
                          <>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                              <p style={{ fontSize:11, color:T.textMuted, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.07em' }}>Amount</p>
                              <p style={{ fontSize:18, fontWeight:700, color:amount?T.text:T.textDim }}>{`KES ${parseFloat(amount||0).toLocaleString()||'0'}`}</p>
                            </div>
                            <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                              {QUICK.map(a => (
                                <button key={a} onClick={() => setAmount(a.toString())}
                                  style={{ flex:1, padding:'7px 4px', borderRadius:6, border:`1px solid ${amount===a.toString()?T.accent:T.border}`, background:amount===a.toString()?`${T.accent}15`:T.surfaceHi, color:amount===a.toString()?T.accent:T.textMuted, fontWeight:600, cursor:'pointer', fontSize:12, fontFamily:'inherit' }}>
                                  +{a>=1000?`${a/1000}K`:a}
                                </button>
                              ))}
                            </div>
                            <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount" min="1"
                              style={{ width:'100%', background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:7, padding:12, color:T.text, fontSize:14, marginBottom:12, fontFamily:'inherit' }}/>
                            {prediction&&amount&&parseFloat(amount)>0 && (
                              <div style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 14px', marginBottom:12 }}>
                                <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:4 }}>
                                  <span style={{ color:T.textMuted }}>Est. return if correct now</span>
                                  <span style={{ color:T.yes, fontWeight:700 }}>{`KES ${payout.toFixed(2)}`}</span>
                                </div>
                                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                                  <span style={{ color:T.textDim }}>Entry fee (2%)</span>
                                 <span style={{ color:T.no }}>− KES {(parseFloat(amount||0) * 0.02).toFixed(2)}</span>
                                </div>
                               
                                {total > 0 ? (
                                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, marginBottom:4 }}>
                                    <span style={{ color:T.textDim }}>Profit</span>
                                    <span style={{ color:profit>=0?T.yes:T.no }}>{`${profit>=0?'+':''}KES ${Math.abs(profit).toFixed(2)}`}</span>
                                  </div>
                                ) : (
                                  <><span style={{ fontSize: 11, color: T.textDim }}>⚡ First trade — return grows as others join</span><br/>
<span style={{ fontSize: 11, color: T.textDim }}>Note 2% resolution fee is included</span>
                                  </>
                                )}
                                <div style={{ display:'flex', justifyContent:'space-between', fontSize:12 }}>
                                  <span style={{ color:T.textDim }}>Shares @ {((calcPayout().price||0.5)*100).toFixed(0)}¢</span>
                                  <span style={{ color:T.textMuted, fontWeight:600 }}>{(calcPayout().shares||0).toFixed(2)} shares</span>
                                </div>
                              </div>
                            )}
                            
    <StakeWarning estimate={estimate} amount={amount} T={T} setAmount={setAmount}/>
  <button onClick={handleBet} disabled={!amount||betLoading}
    style={{ width:'100%', background:!amount?T.surfaceHi:T.accent, color:!amount?T.textDim:'#fff', border:'none', borderRadius:8, padding:14, fontWeight:700, cursor:!amount?'not-allowed':'pointer', fontSize:15, fontFamily:'inherit', transition:'all 0.15s' }}>
    {betLoading ? 'Placing...' : chamaContext ? `Trade as ${chamaContext.chamaName}` : 'Trade'}
  </button>

                            <p style={{ textAlign:'center', color:T.textDim, fontSize:11, marginTop:10 }}>
                              By trading you agree to Mino's <span onClick={() => navigate('/terms')} style={{ color:T.textMuted, cursor:'pointer' }}>Terms of Use</span>
                            </p>
                          </>
                        )}

                        {/* Mini stats */}
                        <div style={{ marginTop:16, paddingTop:16, borderTop:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between' }}>
                          {[{ label:'Total Vol', value: total >= 10000 ? fmt(total) : '<10K', color:T.text },{ label:'24h Vol', value: parseFloat(market.volume_24h||0) >= 10000 ? fmt(market.volume_24h||0) : '<10K', color:T.accent }].map(({ label, value, color }) => (
                            <div key={label} style={{ textAlign:'center' }}>
                              <p style={{ fontSize:10, color:T.textDim, textTransform:'uppercase', letterSpacing:'0.07em', fontWeight:600, marginBottom:3 }}>{label}</p>
                              <p style={{ fontSize:12, fontWeight:700, color }}>{value}</p>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Your positions */}
              {market?.user_positions?.length > 0 && (
                <div style={{ marginTop:12, marginBottom:12, padding:'12px 14px', background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:8 }}>
                  <p style={{ fontSize:11, fontWeight:700, color:T.textMuted, textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>Your Positions</p>
                  {market.user_positions.map((pos, i) => {
                    const label = (pos.display_label || pos.option_label || pos.prediction)?.toUpperCase();
                    const color = pos.prediction === 'yes' ? T.yes : pos.prediction === 'no' ? T.no : OPTION_COLORS[i % OPTION_COLORS.length];
                    return (
                      <div key={i} style={{ marginBottom: i < market.user_positions.length - 1 ? 10 : 0, paddingBottom: i < market.user_positions.length - 1 ? 10 : 0, borderBottom: i < market.user_positions.length - 1 ? `1px solid ${T.border}` : 'none' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:3 }}>
                          <span style={{ fontSize:12, fontWeight:700, color }}>{label}</span>
                          <span style={{ fontSize:12, fontWeight:700, color:T.text }}>KES {parseFloat(pos.total_amount).toLocaleString()}</span>
                        </div>
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <span style={{ fontSize:11, color:T.textDim }}>Est. return if correct now</span>
                          <div style={{ textAlign:'right' }}>
                            <span style={{ fontSize:11, fontWeight:600, color:T.yes, display:'block' }}>
  KES {parseFloat(pos.potential_payout||0).toFixed(2)}
</span>
<span style={{ fontSize:10, color:T.textDim }}>after tax · {parseFloat(pos.total_amount) > 0 ? `${(parseFloat(pos.potential_payout||0) / parseFloat(pos.total_amount)).toFixed(2)}x` : '—'}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <RelatedMarkets category={market.category} currentId={id} navigate={navigate} T={T}/>
              <LiveBetTicker marketId={id} market={market} T={T} token={token}/>
            </div>

          </div>
        </div>
      </div>

      <BottomNav />

      {/* Mobile sticky buy/sell bar */}
      {isMobile && !isClosed && !sheetOpen && (
        <div style={{ position:'fixed', bottom:56, left:0, right:0, zIndex:150, padding:'10px 16px', background:`${T.bg}f5`, backdropFilter:'blur(12px)', borderTop:`1px solid ${T.border}` }}>
          {!isMultiple ? (
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setPrediction('yes'); setBetMode('buy'); setSheetOpen(true); }}
                style={{ flex:1, padding:'12px', borderRadius:8, border:'none', background:T.yes, color:'#0f1011', fontWeight:800, cursor:'pointer', fontSize:14, fontFamily:'inherit', display:'flex', justifyContent:'center', alignItems:'center', gap:6 }}>
                <span>{market.yes_label||'Yes'}</span>
                <span style={{ opacity:0.8 }}>{yesProb}%</span>
              </button>
              <button onClick={() => { setPrediction('no'); setBetMode('buy'); setSheetOpen(true); }}
                style={{ flex:1, padding:'12px', borderRadius:8, border:'none', background:T.no, color:'#fff', fontWeight:800, cursor:'pointer', fontSize:14, fontFamily:'inherit', display:'flex', justifyContent:'center', alignItems:'center', gap:6 }}>
                <span>{market.no_label||'No'}</span>
                <span style={{ opacity:0.8 }}>{100-yesProb}%</span>
              </button>
              {market?.user_positions?.length > 0 && (
                <button onClick={() => { setBetMode('sell'); setSheetOpen(true); }}
                  style={{ padding:'12px 16px', borderRadius:8, border:`1px solid ${T.border}`, background:T.surfaceHi, color:T.textMuted, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
                  Sell
                </button>
              )}
            </div>
          ) : (
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => { setBetMode('buy'); setSheetOpen(true); }}
                style={{ flex:1, padding:'12px', borderRadius:8, border:'none', background:T.accent, color:'#fff', fontWeight:800, cursor:'pointer', fontSize:14, fontFamily:'inherit' }}>
                Trade
              </button>
              {market?.user_positions?.length > 0 && (
                <button onClick={() => { setBetMode('sell'); setSheetOpen(true); }}
                  style={{ padding:'12px 16px', borderRadius:8, border:`1px solid ${T.border}`, background:T.surfaceHi, color:T.textMuted, fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
                  Sell
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mobile bottom sheet */}
      {isMobile && sheetOpen && token && !isClosed && (
        <>
          <div onClick={() => setSheetOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:200, backdropFilter:'blur(2px)' }}/>
          <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:201, background:T.surface, borderTop:`1px solid ${T.border}`, borderRadius:'16px 16px 0 0', paddingBottom:32, animation:'slideUp 0.25s ease' }}>
            {/* Handle */}
            <div style={{ display:'flex', justifyContent:'center', padding:'12px 0 4px' }}>
              <div style={{ width:36, height:4, borderRadius:2, background:T.border }}/>
            </div>
            {/* Header */}
            <div style={{ padding:'10px 16px 14px', borderBottom:`1px solid ${T.border}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontWeight:700, fontSize:15, color:T.text }}>{market.title}</span>
              <button onClick={() => setSheetOpen(false)} style={{ background:T.surfaceHi, border:'none', color:T.textMuted, cursor:'pointer', fontSize:18, width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'inherit' }}>x</button>
            </div>

            {/* Buy / Sell tabs */}
            <div style={{ display:'flex', borderBottom:`1px solid ${T.border}` }}>
              {['buy','sell'].map(tab => (
                <button key={tab} onClick={() => setBetMode(tab)}
                  style={{ flex:1, padding:'12px 0', background:'transparent', border:'none', borderBottom:`2px solid ${betMode===tab?T.accent:'transparent'}`, color:betMode===tab?T.text:T.textMuted, fontWeight:betMode===tab?700:400, cursor:'pointer', fontSize:14, fontFamily:'inherit', textTransform:'capitalize', transition:'all 0.1s' }}>
                  {tab.charAt(0).toUpperCase()+tab.slice(1)}
                </button>
              ))}
            </div>

            <div style={{ padding:'14px 16px', maxHeight:'70vh', overflowY:'auto' }}>
              {successMsg && <div style={{ background:T.yesDim, border:`1px solid ${T.yes}33`, color:T.yes, padding:'10px 12px', borderRadius:7, marginBottom:12, fontSize:13 }}>{successMsg}</div>}
              {error      && <div style={{ background:T.noDim,  border:`1px solid ${T.no}33`,  color:T.no,  padding:'10px 12px', borderRadius:7, marginBottom:12, fontSize:13 }}>{error}</div>}

              {/* ── SELL TAB ── */}
              {betMode === 'sell' ? (
                <SellPanel market={market} token={token} T={T} sellAmount={sellAmount} setSellAmount={setSellAmount} fetchMarket={fetchMarket} updateUser={updateUser} user={user} setPrediction={setPrediction} setSelectedOptionId={setSelectedOptionId} setAmount={setAmount} setBetMode={setBetMode} setSheetOpen={setSheetOpen} setSuccessMsg={setSuccessMsg} setError={setError} isMobile={true}/>
              ) : (
                /* ── BUY TAB ── */
                <>
                  {!isMultiple && (
                    <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                      <button onClick={() => setPrediction(prediction==='yes'?'':'yes')} style={{ flex:1, padding:14, borderRadius:8, border:'none', background:prediction==='yes'?T.yes:T.yesDim, color:prediction==='yes'?'#0f1011':T.yes, fontWeight:800, cursor:'pointer', fontSize:15, fontFamily:'inherit' }}>{market.yes_label||'Yes'} {yesProb}%</button>
                      <button onClick={() => setPrediction(prediction==='no'?'':'no')}  style={{ flex:1, padding:14, borderRadius:8, border:'none', background:prediction==='no'?T.no:T.noDim,   color:prediction==='no'?'#fff':T.no,   fontWeight:800, cursor:'pointer', fontSize:15, fontFamily:'inherit' }}>{market.no_label||'No'} {100-yesProb}%</button>
                    </div>
                  )}
                  {isMultiple && selOption && (
                    <div style={{ marginBottom:16, padding:13, borderRadius:8, background:`${selOption.color}15`, border:`1px solid ${selOption.color}33`, textAlign:'center', fontSize:15, fontWeight:700, color:selOption.color }}>
                      {selOption.label} · {selOption.prob}%
                    </div>
                  )}
                  <div style={{ display:'flex', gap:6, marginBottom:10 }}>
                    {QUICK.map(a => (
                      <button key={a} onClick={() => setAmount(a.toString())} style={{ flex:1, padding:'10px 4px', borderRadius:6, border:`1px solid ${amount===a.toString()?T.accent:T.border}`, background:amount===a.toString()?`${T.accent}15`:T.surfaceHi, color:amount===a.toString()?T.accent:T.textMuted, fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'inherit' }}>
                        +{a>=1000?`${a/1000}K`:a}
                      </button>
                    ))}
                  </div>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount" min="1"
                    style={{ width:'100%', background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:7, padding:13, color:T.text, fontSize:15, marginBottom:12, fontFamily:'inherit' }}/>
                  {prediction && amount && parseFloat(amount) > 0 && (
                    <div style={{ background:T.surfaceHi, border:`1px solid ${T.border}`, borderRadius:7, padding:'10px 14px', marginBottom:12 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                        <span style={{ fontSize:13, color:T.textMuted }}>Est. return if correct now</span>
                        <span style={{ fontSize:13, color:T.yes, fontWeight:700 }}>{`KES ${payout.toFixed(2)}`}</span>
                      </div>
                      {total > 0 ? (
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <span style={{ fontSize:11, color:T.textDim }}>Profit (after 2% entry fee)</span>
                          <span style={{ fontSize:11, color:profit>=0?T.yes:T.no }}>{`${profit>=0?'+':''}KES ${Math.abs(profit).toFixed(2)}`}</span>
                        </div>
                      ) : (
                        <span style={{ fontSize:11, color:T.textDim }}>⚡ First trade — return grows as others join</span>
                      )}
                    </div>
                  )}
                                    <StakeWarning estimate={estimate} amount={amount} T={T} setAmount={setAmount}/>
                  <button onClick={async () => { await handleBet(); setSheetOpen(false); }} disabled={!amount||betLoading}
                    style={{ width:'100%', background:!amount?T.surfaceHi:T.accent, color:!amount?T.textDim:'#fff', border:'none', borderRadius:10, padding:16, fontWeight:800, cursor:!amount?'not-allowed':'pointer', fontSize:16, fontFamily:'inherit' }}>
                    {betLoading ? 'Trading...' : chamaContext ? `Trade as ${chamaContext.chamaName}` : 'Trade'}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {isMobile && sheetOpen && !token && (
        <>
          <div onClick={() => setSheetOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:200 }}/>
          <div style={{ position:'fixed', bottom:0, left:0, right:0, zIndex:201, background:T.surface, borderTop:`1px solid ${T.border}`, borderRadius:'16px 16px 0 0', padding:'24px 20px 40px', animation:'slideUp 0.25s ease' }}>
            <p style={{ fontWeight:700, color:T.text, marginBottom:6, fontSize:16 }}>Sign in to predict</p>
            <p style={{ color:T.textMuted, fontSize:13, marginBottom:20 }}>Create an account or sign in to place your prediction</p>
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              <button onClick={() => navigate('/register')} style={{ background:T.accent, color:'#fff', border:'none', borderRadius:8, padding:14, fontWeight:700, cursor:'pointer', fontSize:15, fontFamily:'inherit' }}>Create account</button>
              <button onClick={() => navigate('/login')} style={{ background:'transparent', color:T.textMuted, border:`1px solid ${T.border}`, borderRadius:8, padding:14, cursor:'pointer', fontSize:15, fontFamily:'inherit' }}>Sign in</button>
            </div>
          </div>
        </>
      )}
    </>
  );
}